import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../authentication/axiosInstance';
import { normalizeOverview } from './overviewModel';
import { subscribeOverview } from './overviewEvents';

export const REFRESH_INTERVAL_MS = 30000;
export const LOCKED_REFRESH_INTERVAL_MS = 120000;
export const CONSISTENCY_RETRY_MS = 3000;
const MAX_CONSISTENCY_RETRIES = 3;

export class SetupChangingError extends Error {
  constructor() {
    super('The setup was being edited while the data loaded. Please try again in a moment.');
    this.name = 'SetupChangingError';
  }
}

export async function fetchOverview(setupId, signal) {
  const base = `/allocation-setups/${encodeURIComponent(setupId)}/`;
  const options = { signal, timeout: 15000 };
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const before = await api.get(base, options);
    const [sites, vehicles, staff] = await Promise.all([
      api.get(`${base}loaders/sites/`, options), api.get(`${base}loaders/vehicles/`, options), api.get(`${base}staff/staff/`, options),
    ]);
    const after = await api.get(base, options);
    if (before.data.updated_at === after.data.updated_at) {
      return { setup: after.data, data: normalizeOverview(sites.data, vehicles.data, staff.data), updatedAt: new Date() };
    }
  }
  throw new SetupChangingError();
}

export default function useAllocationOverview(setupId) {
  const [state, setState] = useState({ loading: true });
  const refreshRef = useRef(() => {});
  useEffect(() => {
    let disposed = false, busy = false, queued = false, timer, retryTimer, controller;
    // Shared records and the lock itself can change outside this browser.
    let locked = false;
    let dirty = false, lastLoadedAt = 0;
    let consistencyRetries = 0;
    setState({ loading: true, setupId });
    async function refresh(force = false) {
      if (disposed || (!force && document.hidden)) return;
      if (busy) { queued = true; return; }
      busy = true;
      dirty = false;
      clearTimeout(retryTimer);
      controller = new AbortController();
      setState(previous => ({ ...previous, loading: true }));
      try {
        const result = await fetchOverview(setupId, controller.signal);
        locked = result.setup?.status === 'archived';
        lastLoadedAt = Date.now();
        consistencyRetries = 0;
        if (!disposed) setState({ ...result, setupId, loading: false, locked });
      } catch (error) {
        controller.abort();
        if (!disposed) {
          dirty = true;
          const unavailable = [403, 404].includes(error.response?.status);
          const changing = error instanceof SetupChangingError;
          const retrying = changing && consistencyRetries < MAX_CONSISTENCY_RETRIES;
          if (retrying) {
            consistencyRetries += 1;
            retryTimer = setTimeout(() => refresh(), CONSISTENCY_RETRY_MS);
          }
          setState(previous => ({ ...(unavailable ? { setupId } : previous), loading: false, unavailable,
            error: unavailable ? 'This allocation setup is unavailable or you do not have access.'
              : retrying ? 'The setup is being edited. Retrying in a few seconds…'
                : changing ? 'The setup is still being edited. Showing the last loaded data; it will refresh again shortly.'
                  : 'Refresh failed. Displayed data may be out of date.',
          }));
        }
      } finally {
        busy = false;
        if (queued && !disposed) { queued = false; refresh(); }
      }
    }
    const schedule = () => { clearTimeout(timer); timer = setTimeout(() => refresh(), 250); };
    const invalidate = () => { dirty = true; if (!document.hidden) schedule(); };
    const unsubscribe = subscribeOverview(setupId, invalidate);
    const due = () => !locked || Date.now() - lastLoadedAt >= LOCKED_REFRESH_INTERVAL_MS;
    const interval = setInterval(() => { if (!locked) refresh(); }, REFRESH_INTERVAL_MS);
    const lockedInterval = setInterval(() => { if (locked) refresh(); }, LOCKED_REFRESH_INTERVAL_MS);
    const resume = () => { if (!document.hidden && (dirty || due())) schedule(); };
    window.addEventListener('focus', resume);
    window.addEventListener('online', invalidate);
    document.addEventListener('visibilitychange', resume);
    refreshRef.current = () => refresh(true);
    refresh();
    return () => {
      disposed = true; controller?.abort(); clearTimeout(timer); clearTimeout(retryTimer); clearInterval(interval); clearInterval(lockedInterval); unsubscribe();
      window.removeEventListener('focus', resume); window.removeEventListener('online', invalidate);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [setupId]);
  const refresh = useCallback(() => refreshRef.current(), []);
  return { ...(String(state.setupId) === String(setupId) ? state : { loading: true }), refresh };
}
